import { Sparkles, CircleCheck, Users, School, Utensils } from "lucide-react";

const t = {
  fr: {
    title: "Solution EcoLunch",
    subtitle: "Une Plateforme. Possibilités Infinies.",
    intro: "EcoLunch remplace tous les systèmes fragmentés par une expérience unifiée.",
    bold: "EcoLunch est LA plateforme :",
    cards: [
      { title: "Pour les Traiteurs", desc: "Configurer les menus, horaires, prix et production en un seul endroit", items: ["Gestion des menus", "Configuration des horaires", "Contrôle des prix", "Rapports de production"] },
      { title: "Pour les Parents", desc: "Commander des repas, payer, gérer les enfants et suivre les services sans effort", items: ["Commande facile", "Paiements sécurisés", "Gestion des enfants", "Suivi des services"] },
      { title: "Pour les Écoles", desc: "Superviser les opérations, voir les commandes et maintenir les normes de qualité", items: ["Supervision des opérations", "Visibilité des commandes", "Contrôle qualité", "Rapports en temps réel"] },
    ],
    diffLabel: "Différenciateur Clé",
    diffTitle: "Avec ÉcoLunch, tout reste simple et connecté.",
    stakeholders: [
      { icon: Users, title: "Les parents :", desc: "conservent le même compte, même s'ils déménagent, changent d'école ou de traiteur. Leur historique, leurs préférences et leurs enfants les suivent automatiquement." },
      { icon: School, title: "Les écoles :", desc: "gardent la même plateforme, peu importe le traiteur en place : même messagerie interne, même interface de gestion, mêmes outils de suivi et de communication." },
      { icon: Utensils, title: "Les traiteurs :", desc: "Retirent ou ajoutent une école de manière automatisée, simple et fluide en un tour de main." },
    ],
    becomes: "ÉcoLunch devient la norme.",
  },
  en: {
    title: "EcoLunch Solution",
    subtitle: "One Platform. Infinite Possibilities.",
    intro: "EcoLunch replaces all fragmented systems with a unified experience.",
    bold: "EcoLunch IS the platform:",
    cards: [
      { title: "For Caterers", desc: "Configure menus, schedules, prices and production in one place", items: ["Menu management", "Schedule configuration", "Price control", "Production reports"] },
      { title: "For Parents", desc: "Order meals, pay, manage children and track services effortlessly", items: ["Easy ordering", "Secure payments", "Child management", "Service tracking"] },
      { title: "For Schools", desc: "Oversee operations, view orders and maintain quality standards", items: ["Operations oversight", "Order visibility", "Quality control", "Real-time reports"] },
    ],
    diffLabel: "Key Differentiator",
    diffTitle: "With EcoLunch, everything stays simple and connected.",
    stakeholders: [
      { icon: Users, title: "Parents:", desc: "keep the same account even when moving, changing schools or caterers. Their history, preferences and children follow them automatically." },
      { icon: School, title: "Schools:", desc: "keep the same platform regardless of the caterer: same internal messaging, same management interface, same tracking tools." },
      { icon: Utensils, title: "Caterers:", desc: "Add or remove a school in an automated, simple and fluid way." },
    ],
    becomes: "EcoLunch becomes the standard.",
  },
};

export default function SolutionSection({ lang }) {
  const c = t[lang];
  return (
    <section id="solution" data-testid="solution-section" className="py-16 sm:py-24 bg-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-primary">
            <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ textShadow: "0 0 30px rgba(182,236,19,0.4)" }}>{c.title}</h2>
          <p className="text-lg sm:text-xl text-gradient-lime font-bold">{c.subtitle}</p>
        </div>
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 text-center">
          <p className="text-base sm:text-lg text-white/90 mb-4">{c.intro}</p>
          <p className="text-xl sm:text-2xl font-bold text-primary">{c.bold}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {c.cards.map((card) => (
            <div key={card.title} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-primary/30 hover:border-primary transition-all duration-300">
              <div className="p-6 pt-6 sm:pt-8 pb-6 sm:pb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-3 sm:mb-4">{card.title}</h3>
                <p className="text-sm sm:text-base text-white/80 mb-4 sm:mb-6">{card.desc}</p>
                <ul className="space-y-2 sm:space-y-3">
                  {card.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CircleCheck className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm text-white/70">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center rounded-md border shadow mb-4 sm:mb-6 bg-primary/20 text-primary border-primary/40 font-bold text-base sm:text-lg px-4 sm:px-6 py-2 sm:py-3">{c.diffLabel}</div>
            <p className="text-xl sm:text-2xl font-bold text-white px-4" style={{ textShadow: "0 0 30px rgba(182,236,19,0.5)" }}>{c.diffTitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-10">
            {c.stakeholders.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-xl border text-card-foreground shadow bg-dark border-primary/30 hover:border-primary transition-all duration-300">
                  <div className="p-6 pt-6 pb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                      <h4 className="text-lg sm:text-xl font-bold text-primary">{s.title}</h4>
                    </div>
                    <p className="text-sm text-white/80 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/50 glow-strong">
            <div className="p-6 py-6 sm:py-8">
              <p className="text-3xl sm:text-4xl lg:text-5xl font-black text-primary text-center px-4" style={{ textShadow: "0 0 50px rgba(182,236,19,0.9)" }}>{c.becomes}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
